import { useEffect } from 'react';
import { Outlet } from 'react-router';

// react-redux
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';

// import Customization from '../Customization';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { setMenu } from 'store/features/customizationSlice';
import bottomRightBrand from 'assets/images/brand/bottom_brand.png';

// assets
import IconChevronRight from '@tabler/icons-react/dist/esm/icons/IconChevronRight';

// utils
import useAccessRoleSync from 'hooks-ts/useAccessRoleSync';
import FeatureFlagsProvider from 'hooks-ts/FeatureFlagsProvider';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  ...theme.typography.mainContent,

  variants: [
    {
      props: ({ open }) => !open,

      style: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
          width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
          width: `calc(100% - ${drawerWidth}px)`,
          padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
          width: `calc(100% - ${drawerWidth}px)`,
          padding: '16px',
          marginRight: '10px'
        }
      }
    },
    {
      props: ({ open }) => open,

      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`
      }
    }
  ]
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);

  const dispatch = useDispatch();

  useAccessRoleSync();

  const handleLeftDrawerToggle = () => {
    dispatch(setMenu({ opened: !leftDrawerOpened }));
  };

  useEffect(() => {
    dispatch(setMenu({ opened: !matchDownMd }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMd]);

  return (
    <FeatureFlagsProvider>
      <Box sx={{ display: 'flex', pl: 'var(--safe-area-left)', pr: 'var(--safe-area-right)' }}>
        <CssBaseline />
        {/* header */}
        <AppBar
          enableColorOnDark
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            bgcolor: theme.palette.background.default,
            transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
          }}
        >
          <Toolbar sx={{ height: 91, borderBottom: 1, borderColor: 'grey.200', padding: '0px !important' }}>
            <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
          </Toolbar>
        </AppBar>

        {/* drawer */}
        <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

        {/* main content */}
        {/* <Main theme={theme} open={leftDrawerOpened}> */}
        <Main open={leftDrawerOpened} sx={{ paddingTop: 3, paddingBottom: 'calc(20px + var(--safe-area-bottom))' }}>
          {/* breadcrumb */}
          <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
          <Outlet />
          {/* Brand */}
          <Box sx={{ width: 'fit-content', mt: 3, ml: 'auto' }}>
            <img src={bottomRightBrand} alt="bottom right brand" />
          </Box>
        </Main>
        {/* <Customization /> */}
      </Box>
      {/* Disabled for now. */}
      {/* <QuickActionFab /> */}
    </FeatureFlagsProvider>
  );
};

export default MainLayout;
